import { useEffect, useRef, useState } from "react";
import SEO, { organizationSchema, websiteSchema } from "../Components/SEO";
import TrackerBillingGate from "../Components/TrackerBillingGate";
import { completeTwitchConnection } from "../services/twitchStats";
import TheNav from "../Components/navbar";
import { StatsState } from "../Components/StatsUI";
import TwitchStats from "../Components/TwitchStats";
import TikTokStats from "../Components/TikTokStats";
import InstagramTestStats from "../Components/InstagramTestStats";
import { useAuth } from "../Context/AuthContext";
import StatsLanding from "../Components/StatsLanding";
import { trackStatsEvent, trackStatsVisit } from "../utils/statsAnalytics";
import { trackTikTokStatsEvent } from "../utils/tiktokPixel";
import { getInstagramTestStatus } from "../services/instagramTest";
import { initializeMetaPixel } from "../utils/metaPixel";

const statsDescription = "Kre8 is a social media analytics tool for creators, streamers, marketers, and campaign managers. Compare TikTok video analytics, Instagram Reels performance, and Twitch stream stats to make stronger content decisions.";
const statsSchema = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "Kre8 Social Media Stats Tracker",
  url: "https://kre8content.com/stats",
  description: statsDescription,
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web browser",
  browserRequirements: "Requires JavaScript",
  audience: { "@type": "Audience", audienceType: "Twitch streamers, content creators, social media managers, marketers, and campaign managers" },
  featureList: [
    "TikTok video analytics",
    "Instagram Reels analytics",
    "Twitch stream stats",
    "Cross-platform content performance tracking",
  ],
  offers: { "@type": "Offer", price: "10.00", priceCurrency: "USD", description: "7-day free trial with card required, then $10 USD/month automatically unless canceled. Cancel anytime." },
  publisher: { "@type": "Organization", name: "Kre8 Content", url: "https://kre8content.com" },
};

export default function Stats() {
  const { currentUser, authLoading } = useAuth();
  const visitReported = useRef(false);
  useEffect(() => {
    if (authLoading || visitReported.current) return;
    visitReported.current = true;
    trackStatsVisit(Boolean(currentUser));
    trackTikTokStatsEvent("ViewContent");
    if (!currentUser) initializeMetaPixel();
  }, [authLoading, currentUser]);
  useEffect(() => {
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    if (fragment.has("access_token") || fragment.has("error")) completeTwitchConnection().catch(() => {});
  }, []);
  const [instagramAccess, setInstagramAccess] = useState(null);
  const [tab, setTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const requested = params.get("tab") || params.get("platform");
    if (params.has("tiktok")) return "tiktok";
    if (params.has("instagram")) return "instagram";
    if (fragment.has("access_token") || fragment.has("error") || (params.has("error") && params.has("state"))) return "twitch";
    return ["tiktok", "instagram", "twitch"].includes(requested) ? requested : "tiktok";
  });
  useEffect(() => {
    let active = true;
    setInstagramAccess(null);
    if (currentUser) getInstagramTestStatus(currentUser).then((access) => {
      if (active) setInstagramAccess(access);
    }).catch(() => { if (active) setInstagramAccess({ allowed: false, failed: true }); });
    return () => { active = false; };
  }, [currentUser]);
  const selectedTab = tab;
  const tabs = [['tiktok', 'TikTok'], ['instagram', 'Instagram'], ['twitch', 'Twitch']];
  const selectTab = (next) => {
    setTab(next);
    trackStatsEvent("stats_platform_selected", { platform: next });
    const url = new URL(window.location.href);
    url.searchParams.set("tab", next);
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  };
  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-[#07060d] text-white">
      <SEO title="Social Media Analytics Tool for Creators | Kre8 Content" description={statsDescription} canonicalPath="/stats" structuredData={[statsSchema, organizationSchema, websiteSchema]} />
      <TheNav />
      <main className="relative isolate">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[520px]" style={{ backgroundImage: "radial-gradient(50% 60% at 15% 0%, #9146ff1f, transparent 70%)" }} />
        <div className="relative mx-auto w-full max-w-[1600px] px-5 pb-24 sm:px-8 lg:w-[85%] lg:px-0">
          {authLoading ? <div role="status" className="py-16 text-sm text-white/55">Loading…</div> : !currentUser ? <StatsLanding /> : <section className="stats-workspace py-8 sm:py-12">
            <p className="stats-eyebrow">Turn performance data into your next growth move</p>
            <h1 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">Social Media Stats Tracker</h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/55">Use views, engagement, watch time, retention, and broadcast history to find what earns attention, then make smarter content decisions to grow.</p>
            <TrackerBillingGate key={currentUser.uid} user={currentUser}>
            <div role="tablist" aria-label="Analytics platform" className="stats-platform-tabs">
              {tabs.map(([key, label]) => <button key={key} id={`tab-${key}`} type="button" role="tab" aria-selected={selectedTab === key} aria-controls={`panel-${key}`} tabIndex={selectedTab === key ? 0 : -1} onKeyDown={(event) => {
                if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
                event.preventDefault();
                const index = tabs.findIndex(([value]) => value === key);
                const next = tabs[event.key === "Home" ? 0 : event.key === "End" ? tabs.length - 1 : (index + (event.key === "ArrowLeft" ? -1 : 1) + tabs.length) % tabs.length][0];
                selectTab(next);
                document.getElementById(`tab-${next}`)?.focus();
              }} onClick={() => selectTab(key)}>{label}</button>)}
            </div>
            <div className="stats-data-surface">
              <div role="tabpanel" id="panel-tiktok" aria-labelledby="tab-tiktok" hidden={selectedTab !== "tiktok"}><TikTokStats key={currentUser?.uid || "guest"} visible={selectedTab === "tiktok"} /></div>
              <div role="tabpanel" id="panel-twitch" aria-labelledby="tab-twitch" hidden={selectedTab !== "twitch"}><TwitchStats visible={selectedTab === "twitch"} /></div>
              {selectedTab === "instagram" && <div role="tabpanel" id="panel-instagram" aria-labelledby="tab-instagram">{instagramAccess?.allowed && currentUser ? <InstagramTestStats key={currentUser.uid} access={instagramAccess} /> : <section aria-label="Instagram"><StatsState platform="Instagram" href={!currentUser ? "/login?next=%2Fstats%3Ftab%3Dinstagram" : undefined} loading={Boolean(currentUser) && instagramAccess === null} error={instagramAccess?.failed} disabled={Boolean(currentUser)} title={currentUser && instagramAccess !== null ? "Instagram connections aren't available for this account yet." : undefined} /></section>}</div>}
            </div>
            </TrackerBillingGate>
            <p className="mt-4 text-xs text-white/45">Currently having data issues with Meta, will be resolved shortly. Please focus on TikTok for now.</p>
          </section>}
        </div>
      </main>
    </div>
  );
}
